import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Row, Col, Select, Input, DatePicker, message } from 'antd';
import DataTable from 'react-data-table-component';
import queryString from 'query-string';
import OrderService from 'services/order';
import DisbursementService from 'services/disbursement';

import { ProfileContext } from 'contexts/profile';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import addDay from 'utils/addDay';

import { get } from 'lodash';

import PdButton from 'components/pd-button';

import './style.scss';

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD';

export function Disbursements() {
  const location = useLocation();
  const [loadStatus, setLoadStatus] = useState(true);
  const [couriers, setCouriers] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [orderList, setOrderList] = useState([]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(1);
  const [sortBy, setSortBy] = useState('ordered_at desc');
  const parsed = queryString.parse(location.search);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [endDatePick, setEndDate] = useState('');

  const { fetchProfile } = useContext(ProfileContext);

  const customElement = (row) => (
    <div className="button-table">
      <Link to={`/disbursements/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32, width: 100 }}>
          See Detail
        </PdButton>
      </Link>
    </div>
  );

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Order No.',
      selector: row => row.formatted_id,
      width: '10%',
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.ordered_at).format('DD/MM/YYYY');
      },
      width: '15%',
      center: true,
    },
    {
      name: 'Customer',
      selector: row => row.user.full_name,
      width: '20%',
    },
    {
      name: 'Courier',
      selector: row => row.courier.title,
      width: '20%',
    },

    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        let str = row.status.split('_');
        str = str.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        });
        return str.join(' ');
      },
      width: '15%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '20%',
    },
  ]);

  async function fetchOrderList(
    page = 1,
    total = 10,
    query,
    sort,
    courier,
    startDate,
    endDate,
    filter
  ) {
    setLoadStatus(true);
    let newEndDate = endDate;
    if (newEndDate) {
      newEndDate = addDay(endDate, 1);
    }
    try {
      const { data } = await DisbursementService.getOrderList(
        page,
        total,
        query,
        startDate,
        newEndDate,
        sort,
        courier,
        filter
      );
      const orderData = data.data;
      const metadata = data.metadata;
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setOrderList(orderData);
    } finally {
      setLoadStatus(false);
    }
  }

  async function getCourierList() {
    try {
      const { data } = await OrderService.getCourierList();
      const CourierList = data.data;
      setCouriers(CourierList);
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/disbursements',
      search: stringified,
    });
  }

  function onChangeDate(dates, datesString) {
    const startDate = datesString[0];
    if (startDate === '') {
      delete parsed['startDate'];
    } else {
      parsed['startDate'] = startDate;
    }

    const endDate = datesString[1];
    if (endDate === '') {
      delete parsed['endDate'];
    } else {
      parsed['endDate'] = endDate;
    }

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/disbursements',
      search: stringified,
    });
  }
  function filterOrder(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/disbursements',
      search: stringified,
    });
  }

  function resetFilter() {
    delete parsed['page'];
    delete parsed['q'];
    delete parsed['status'];
    delete parsed['sortBy'];
    delete parsed['courier'];
    delete parsed['startDate'];
    delete parsed['endDate'];
    delete parsed['limit'];

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/disbursements',
      search: stringified,
    });
  }

  useEffect(() => {
    getCourierList();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) {
      setSearchValue(parsed['q']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/returns',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    if (parsed['sortBy']) {
      setSortBy(parsed['sortBy']);
    } else {
      setSortBy('ordered_at desc');
    }

    if (parsed['courier']) {
      setSelectedCourier(parsed['courier']);
    } else {
      setSelectedCourier(0);
    }

    if (parsed['startDate']) {
      setStartDate(parsed['startDate']);
    } else {
      setStartDate('');
    }

    if (parsed['endDate']) {
      setEndDate(parsed['endDate']);
    } else {
      setEndDate('');
    }

    fetchOrderList(
      parsed['page'],
      10,
      parsed['q'],
      parsed['sortBy'],
      parsed['courier'],
      parsed['startDate'],
      parsed['endDate']
    );
  }, [
    parsed['page'],
    parsed['q'],
    parsed['sortBy'],
    parsed['courier'],
    parsed['startDate'],
    parsed['endDate'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="pd-cms-shipments main-container">
      <div className="content">
        <div className="pageTitle">Disbursements</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Order List"
                onSearch={(val) => filterOrder('q', val)}
              />
            </Col>
            <Col span={24}>
              <Row gutter={18}>
                <Col span={7} className="mt-1">
                  <RangePicker
                    className="w-full"
                    format={dateFormat}
                    onChange={onChangeDate}
                    value={[
                      startDatePick && dayjs(startDatePick, dateFormat),
                      endDatePick && dayjs(endDatePick, dateFormat),
                    ]}
                  />
                </Col>
                <Col span={4} className="mt-1">
                  <Select
                    style={{ width: '100%' }}
                    value={Number(selectedCourier)}
                    onChange={(val) => filterOrder('courier', val)}>
                    <Option value={0}>All Couriers</Option>
                    {couriers.map((courier, i) => (
                      <Option key={i} value={courier.id}>
                        {courier.title}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={3} className="mt-1">
                  <Select
                    value={sortBy}
                    style={{ width: '100%' }}
                    onChange={(val) => filterOrder('sortBy', val)}>
                    <Option value="ordered_at desc">Latest</Option>
                    <Option value="ordered_at asc">Oldest</Option>
                    <Option value="status">Status</Option>
                  </Select>
                </Col>
                <Col span={4} className="mt-1">
                  <span className="reset-filter" onClick={resetFilter}>
                    Reset Filter
                  </span>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <>
                <Col xs={24}>
                  <DataTable
                    columns={columns}
                    data={orderList}
                    progressPending={loadStatus}
                    noHeader
                    highlightOnHover
                    striped
                  />
                </Col>

                <Col span={8} offset={8}>
                  <div className="center-button">
                    {page !== '1' && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) - 1)}>
                        Previous
                      </PdButton>
                    )}
                    {nextPage !== -1 && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) + 1)}>
                        Next
                      </PdButton>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { Disbursements };
